import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import styles from '../sso-config/SsoConfig.module.scss'
import {
  Accordion,
  AccordionTypes,
  Button,
  ButtonType,
  FormFieldSize,
  FormGroup,
  Input,
  Switch,
  Tooltip,
  TextArea
} from '@digicert/dcone-common-ui'
import { ReactComponent as StepOneIcon } from '../../../assets/images/step-1.svg'
import { ReactComponent as StepTwoIcon } from '../../../assets/images/step-2.svg'
import {ReactComponent as StepThreeIcon} from '../../../assets/images/step-3.svg'
import { translate } from '../../helpers/utils'
import { connect } from 'react-redux'
import { IAppState } from '../../../core/store/store'
import { DISABLED, ENABLED } from '../../../core/constants'
import { Link, useParams } from 'react-router-dom'
import { fetchTenantById } from "../../../secure-pages/tenants/Tenants.actions";
import { OpenIdConnectIntegrationProps } from './OidcConfig.types'
import { createAccountSignIn, getAccountSignIn } from 'secure-pages/tenants/create-account-sign-in/AccountSignIn.actions'
import { updateIdpMetadata } from 'secure-pages/tenants/tenant/open-id-connect-integration/OpenIdConnectIntegration.actions'
import { ConfigResponse } from 'secure-pages/tenants/tenant/open-id-connect-integration/OpenIdConnectIntegration.types'
import { Translate } from 'react-localize-redux'
import { faCopy, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cn from 'classnames'
import { isEmpty } from 'lodash'
import { faPencil } from '@fortawesome/pro-light-svg-icons'

const OidcConfig = (props: OpenIdConnectIntegrationProps) => {
    const { id } = useParams()
    const { createAccountSignIn, fetchTenantById, getAccountSignIn, updateIdpMetadata, onCancel, ssoOidc, onChange, setOidcEnabled, primaryAccountId } = props
    const [providerUrl, setProviderUrl] = useState('')
    const [clientSecret, setClientSecret] = useState('')
    const [authConfigStatus, setAuthConfigStatus] = useState(undefined)
    const [clientId, setClientId] = useState('')
    const [tokenAudience, setTokenAudience] = useState('')
    const [account, setAccount] = useState(props.account)
    const [callBackUrl, setCallBackUrl] = useState('')
    const [loginEndpoint, setLoginEndPoint] = useState('')
    const [logoutEndpoint, setLogoutEndpoint] = useState('')
    const [status, setStatus] = useState('DISABLED')
    const [federationId, setFederationId] = useState('')
    const [isSsoOidcEnabled, setSsoOidcEnabled] = useState(ssoOidc)
    const [accountSignInResponse, setAccountSignInResponse] = useState<any>(undefined)
    const [copied, setCopied] = useState<any>()
    const [editingClientSecret, setEditingClientSecret] = useState(false)

    const payload = {
        auth_type: 'OIDC',
        account_id: !id ? primaryAccountId : id
    }

    const cancelOidc = () => {
        if (onCancel) {
            onCancel()
        }
    }

    useEffect(() => {
        if(accountSignInResponse !== undefined) {
            setCallBackUrl(accountSignInResponse.service_provider_config.callback_url)
            setLoginEndPoint(accountSignInResponse.service_provider_config.login_endpoint)
            setLogoutEndpoint(accountSignInResponse.service_provider_config.logout_endpoint)
            setStatus(accountSignInResponse.status)
            if (isEmpty(accountSignInResponse.identity_provider_config) ){
                setProviderUrl('')
                setClientId('')
                setClientSecret('')
                setTokenAudience('')
            } else {
                setProviderUrl(accountSignInResponse.identity_provider_config.provider_url)
                setClientId(accountSignInResponse.identity_provider_config.client_id)
                setClientSecret(accountSignInResponse.identity_provider_config.client_secret)
                setTokenAudience(accountSignInResponse.identity_provider_config.token_audience)
                setEditingClientSecret(false)
            }
        }
        const signin = account?.sign_in_methods.find((item: { signInMethod: string }) => item.signInMethod === 'singleSignOnOidc')
        setSsoOidcEnabled(signin?.status === ENABLED)
    }, [onCancel])

    const handleSwitchChange = useCallback(
        (checked: boolean) => {
            setSsoOidcEnabled(checked)
            if (checked) {
                setStatus('ENABLED')
            } else {
                setStatus('DISABLED')
            }
        }, [isSsoOidcEnabled]
    )

    const setOidcInfo = (data) => {
        if (data.auth_config && data.auth_config.find(item => item.type === 'OIDC')) {
            setAuthConfigStatus(data.auth_config?.find(item => item.type === 'OIDC').status)
        }

        if (typeof data.auth_config === 'undefined' || data.auth_config == null || data.auth_config.find(item => item.type === 'OIDC') == null) {
            createAccountSignIn(payload).then((response: ConfigResponse) => {
                setAccountSignInResponse(response)
                setFederationId(response.id)
                setCallBackUrl(response.service_provider_config.callback_url)
                setLoginEndPoint(response.service_provider_config.login_endpoint)
                setLogoutEndpoint(response.service_provider_config.logout_endpoint)
                setStatus(response.status)
            })
        }

        else {
            const federationProvider = data.auth_config ? data.auth_config.find(item => item.type === 'OIDC') : undefined
            const federationId = federationProvider ? federationProvider.id : ''
            setFederationId(federationId)
            getAccountSignIn(federationId).then((res: any) => {
                setAccountSignInResponse(res)
                setCallBackUrl(res.service_provider_config.callback_url)
                setLoginEndPoint(res.service_provider_config.login_endpoint)
                setLogoutEndpoint(res.service_provider_config.logout_endpoint)
                setStatus(res.status)
                setProviderUrl(res.identity_provider_config.provider_url)
                setClientId(res.identity_provider_config.client_id)
                setClientSecret(res.identity_provider_config.client_secret)
                setTokenAudience(res.identity_provider_config.token_audience)
            })
        }
    }

    useEffect(() => {
        if (!account) {
            fetchTenantById(!id ? primaryAccountId : id).then((res: any) => {
                setAccount(res)
            })
        }

        else {
            setOidcInfo(account)
        }
    }, [account])


    const onTokenAudienceChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTokenAudience(event.target.value)
    }

    const onClientIdChange = (event: ChangeEvent<HTMLInputElement>) => {
        setClientId(event.target.value)
    }

    const onClientSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
        setClientSecret(event.target.value)
    }

    const onProviderUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setProviderUrl(event.target.value)
    }

    const onSubmit = async (e) => {
        e.preventDefault()
        const payload =
        {
            auth_type: 'OIDC',
            account_id: account && account.id,
            identity_provider_config: {
                provider_url: providerUrl,
                token_audience: tokenAudience,
                client_id: clientId,
                client_secret: clientSecret
            },
            status: status
        }
        updateIdpMetadata(payload, federationId).then(() => {
            fetchTenantById(id === undefined ? primaryAccountId : id).then((response) => {
                    const res = response?.sign_in_methods.find((item: { signInMethod: string }) => item.signInMethod === 'singleSignOnOidc')
                    if (res) {
                      setSsoOidcEnabled(res.status === ENABLED)
                      setAccount(response)
                      setOidcEnabled(res.status === ENABLED)
                    }
            })
            if (onChange) {
                onChange(isSsoOidcEnabled)
            }
        })
    }

    const editClientSecret = (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()
      setEditingClientSecret(true)
      setClientSecret('')
    }

    const connectDigiCertToIdpView = () => {
        return (
          <div className={styles.connectIdp}>
            <p>{translate('openIdConnectIntegration.stepOne')}</p>
            <FormGroup>
                <Input
                    label={translate('openIdConnectIntegration.form.providerUrl')}
                    size={FormFieldSize.XXL}
                    minLength={1}
                    maxLength={256}
                    value={providerUrl}
                    onChange={onProviderUrlChange}
                    id='providerUrl'
                />
            </FormGroup>
            <FormGroup>
                <Input
                    label={translate('openIdConnectIntegration.form.clientId')}
                    size={FormFieldSize.XXL}
                    minLength={1}
                    maxLength={256}
                    value={clientId}
                    onChange={onClientIdChange}
                    id='clientId'
                />
            </FormGroup>
            <FormGroup>
                <Input
                    label={<>
                    <Translate id={'openIdConnectIntegration.form.clientSecret'}/>
                    {authConfigStatus && authConfigStatus !== 'INITIAL' && !editingClientSecret &&(
                            <span
                              className={styles.editClientSecret}
                              onClick={editClientSecret}
                            >
                              <FontAwesomeIcon icon={faPencil}/>
                            </span>
                          )}
                    </>}
                    size={FormFieldSize.XXL}
                    minLength={1}
                    maxLength={256}
                    value={clientSecret}
                    onChange={onClientSecretChange}
                    disabled={authConfigStatus && authConfigStatus !== 'INITIAL' && !editingClientSecret}
                    id='clientSecret'
                />
            </FormGroup>
            <FormGroup>
                <Input
                    label={translate('openIdConnectIntegration.form.tokenAudience')}
                    size={FormFieldSize.XXL}
                    minLength={1}
                    maxLength={256}
                    value={tokenAudience}
                    onChange={onTokenAudienceChange}
                    id='tokenAudience'
                />
            </FormGroup>
          </div>
        )
    }
  const copyToClipboard = (textToCopy, copiedUrl): void => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied({ [copiedUrl]: true })
      setTimeout(() => setCopied({ [copiedUrl]: false }), 1000)
    });
  }

  const connectIdpToDigicertView = () => {
    return (
      <div className={styles.connectDigiCert}>
        <p>{translate('openIdConnectIntegration.stepTwo')}</p>
        <TextArea
          size={FormFieldSize.XXL}
          value={callBackUrl}
          label={translate('openIdConnectIntegration.url1')}
          readOnly
        />
        {!copied?.url1 ? <div className={styles.copyToClipBoardContainer} onClick={() => copyToClipboard(callBackUrl,'url1')}>
          <FontAwesomeIcon size='lg' icon={faCopy} />
          <Translate id='common.form.buttons.copy' />
        </div> :
          <div className={cn(styles.copyToClipBoardContainer,styles.copiedMsg)}>
            <FontAwesomeIcon size='lg' icon={faCheck} />
            <Translate id='openIdConnectIntegration.copied' />
          </div>
        }

        <TextArea
          size={FormFieldSize.XXL}
          label={translate('openIdConnectIntegration.url2')}
          value={loginEndpoint}
          readOnly
        />
        {!copied?.url2 ? <div className={styles.copyToClipBoardContainer} onClick={() => copyToClipboard(loginEndpoint, 'url2')}>
          <FontAwesomeIcon size='lg' icon={faCopy} />
          <Translate id='common.form.buttons.copy' />
        </div> :
          <div className={cn(styles.copyToClipBoardContainer,styles.copiedMsg)}>
            <FontAwesomeIcon size='lg' icon={faCheck} />
            <Translate id='openIdConnectIntegration.copied' />
          </div>
        }
        <TextArea
          size={FormFieldSize.XXL}
          value={logoutEndpoint}
          label={translate('openIdConnectIntegration.url3')}
        />
        {!copied?.url3 ? <div className={styles.copyToClipBoardContainer} onClick={() => copyToClipboard(logoutEndpoint,'url3')}>
          <FontAwesomeIcon size='lg' icon={faCopy} />
          <Translate id='common.form.buttons.copy' />
        </div> :
          <div className={cn(styles.copyToClipBoardContainer,styles.copiedMsg)}>
            <FontAwesomeIcon size='lg' icon={faCheck} />
            <Translate id='openIdConnectIntegration.copied' />
          </div>
        }
      </div>
    )
  }

      const enableDisableOidcView = () => {
        const standardSignIn = account?.sign_in_methods.find(item => item.signInMethod === 'standard')
        return (
          <div className={styles.sso}>
            <div className={styles.content}>
              <Tooltip
                autoAdjustOverflow
                title={translate('ssoSaml.disablingSsoMessage')}
                placement='right'
                getPopupContainer={(triggerNode) => triggerNode}
                open={!isSsoOidcEnabled && standardSignIn?.status === DISABLED}
              >
                <Switch
                  disabled={standardSignIn?.status === DISABLED}
                  checkedChildren={translate('common.form.buttons.on')}
                  unCheckedChildren={translate('common.form.buttons.off')}
                  onChange={handleSwitchChange}
                  checked={isSsoOidcEnabled}
                />
              </Tooltip>
            </div>
          </div>
        )
      }

    return (
        <div className={styles.ssoConfig}>
            <Accordion
                showArrow
                type={AccordionTypes.BORDERED}
                oneByOne={false}
                accordionData={[
                    {
                        headline: translate('ssoSaml.connectIdp') as string,
                        key: '1',
                        managerIcon: <StepOneIcon />,
                        view: connectDigiCertToIdpView(),
                        anchorId: 'step-1',
                        notCollapsible: false,
                        disablePanel: false
                    },
                    {
                        headline: translate('ssoSaml.connectDigiCert') as string,
                        key: '2',
                        managerIcon: <StepTwoIcon />,
                        view: connectIdpToDigicertView(),
                        anchorId: 'step-2',
                        notCollapsible: false,
                        disablePanel: false
                    },
                    {
                        headline: translate('ssoSaml.enableDisableSsoWithOidc') as string,
                        key: '3',
                        managerIcon: <StepThreeIcon />,
                        view: enableDisableOidcView(),
                        anchorId: 'step-3',
                        notCollapsible: false,
                        disablePanel: false
                    }
                ]}
            />
            <div className={styles.actions}>
                <Button buttonType={ButtonType.SECONDARY} onClick={cancelOidc}>{translate('common.form.buttons.cancelBtn')}</Button>
                <Button buttonType={ButtonType.PRIMARY} onClick={onSubmit}>{translate('common.form.buttons.saveConfiguration')}</Button>
                <Link
                    to={{ pathname: 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/account-manager/sign-in-methods/single-sign-on-with-oidc.html' }}
                    target='_blank'>
                    {translate('common.form.buttons.needHelp') as string}
                </Link>
            </div>
        </div>
    )
}

export default connect(
  (state: IAppState) => ({
  }),
  { createAccountSignIn, fetchTenantById, getAccountSignIn, updateIdpMetadata }
)(OidcConfig)
